import React, { useEffect } from 'react'
import {
  ArrowDownCircle,
  FileText,
  PlusCircle,
} from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { persistor } from 'app/store'

import CardBankAccount from 'components/CardBankAccount'
import { useFetchSaldoQuery } from 'features/private-views/dashboard/dashboardApiSlice'
import { useFetchWithdrawalQuery } from 'features/private-views/withdraw/withdrawApiSlice'
import { addBalance, toggleSidebarWithdraw } from 'features/private-views/dashboard/dashboardSlice'
import { setAlertMsg, toggleAlert } from 'features/public-views/login/loginSlice'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import { BUSINESS_TOPUP, MANAGE } from 'utils/subjectActions'
import { COLOR_MAP } from 'config/templateConfig'

import uangBelanjaIcon from 'assets/icons/uang-belanja-icon.svg'
import uangUsahaIcon from 'assets/icons/uang-usaha-icon.svg'
import talanginIcon from 'assets/icons/talangin-icon.svg'

// Shared data hook for financial cards
export const useFinancialData = () => {
  const accessTopup = useAbilityAccess({
    action: MANAGE,
    subject: BUSINESS_TOPUP,
  })
  
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const profile = useOutletContext()
  
  const { role } = useSelector((s) => s.auth)
  const { isProcess } = useSelector((e) => e.withdraw)
  const { lateInvoice } = useSelector((s) => s.talanginInvoice)
  
  const { isLoading, isSuccess, data, isError } = useFetchSaldoQuery()
  const {
    data: dataWithdraw,
  } = useFetchWithdrawalQuery()

  // Auto-sync behavior (matches SectionCards logic)
  useEffect(() => {
    if (isSuccess && data) {
      dispatch(addBalance(data))
    }
    if (isError) {
      persistor.purge()
      navigate('/login')
      dispatch(toggleAlert(true))
      dispatch(setAlertMsg('Ada masalah koneksi, silakan login kembali'))
    }
  }, [isError, navigate, data, dispatch, isSuccess])

  const toggleSidebar = () => dispatch(toggleSidebarWithdraw())

  const isWithdrawProcessing = isProcess || dataWithdraw?.items?.some(
    (wd) =>
      wd?.status === 'Dalam Proses' ||
      (role === 'Staff' && wd?.status === 'Menunggu Approval Admin') ||
      (role === 'Admin' && wd?.status === 'Menunggu Approval Admin')
  )

  const processStatus = dataWithdraw?.items
    ?.map((item) => {
      if (item.status === 'Dalam Proses') return 'Penarikan dalam proses'
      if (role === 'Staff' && item.status === 'Menunggu Approval Admin') return 'Penarikan menunggu approval admin'
      if (role === 'Admin' && item.status === 'Menunggu Approval Admin') return 'Ada penarikan membutuhkan approval'
      return null
    })
    .filter(Boolean)?.[0] || ''

  return {
    accessTopup,
    profile,
    role,
    navigate,
    toggleSidebar,
    isWithdrawProcessing,
    processStatus,
    lateInvoice,
    isLoading,
    isSuccess,
    data,
    dataWithdraw
  }
}

// ---------------------------------------------------------------------------
// INDIVIDUAL WIDGETS
// ---------------------------------------------------------------------------

export const WidgetUangBelanja = ({ params = {}, useDefaultStyle }) => {
  const { isLoading, isSuccess, data, profile, accessTopup, navigate } = useFinancialData()
  const isDefaultTemplate = !!useDefaultStyle
  
  return (
    <CardBankAccount
      cardThemeColor={isDefaultTemplate ? undefined : (COLOR_MAP[params.color] || "#2563EB")}
      iconCard={isDefaultTemplate ? uangBelanjaIcon : undefined}
      iconName={isDefaultTemplate ? null : "old-icons-box-out"}
      title="Uang Belanja"
      testId="widget-uang-belanja"
      btnTitle="Tambah Saldo"
      btnIcon={<PlusCircle size={16} className="me-2" />}
      bankAmount={data?.wallet1}
      isLoading={isLoading}
      isSuccess={isSuccess}
      isDisable={profile?.data?.ownerData?.status !== 'VERIFIED' || !accessTopup}
      handleClick={() => navigate('/topup')}
      onClickHeader={() => navigate('/uang-belanja')}
    />
  )
}

export const WidgetUangUsaha = ({ params = {}, useDefaultStyle }) => {
  const { 
    isLoading, isSuccess, data, navigate, toggleSidebar, 
    isWithdrawProcessing, processStatus, profile
  } = useFinancialData()
  const isDefaultTemplate = !!useDefaultStyle
  
  return (
    <CardBankAccount
      cardThemeColor={isDefaultTemplate ? undefined : (COLOR_MAP[params.color] || "#0891B2")}
      iconCard={isDefaultTemplate ? uangUsahaIcon : undefined}
      iconName={isDefaultTemplate ? null : "old-icons-box-in"}
      title="Uang Usaha"
      testId="widget-uang-usaha"
      btnTitle="Lihat Uang Usaha"
      btnIcon={<ArrowDownCircle size={16} className="me-2" />}
      bankAmount={data?.wallet2}
      isLoading={isLoading}
      isSuccess={isSuccess}
      isDisable={false}
      handleClick={() => {
        if (isWithdrawProcessing) {
          toggleSidebar()
        } else {
          navigate('/penarikan-uang')
        }
      }}
      isProcess={isWithdrawProcessing}
      processMsg={processStatus}
    />
  )
}

export const WidgetTalangin = ({ params = {}, useDefaultStyle }) => {
  const { isLoading, isSuccess, data, navigate, lateInvoice, profile } = useFinancialData()
  const isDefaultTemplate = !!useDefaultStyle
  
  return (
    <CardBankAccount
      cardThemeColor={isDefaultTemplate ? undefined : (COLOR_MAP[params.color] || "#EA580C")}
      iconCard={isDefaultTemplate ? talanginIcon : undefined}
      iconName={isDefaultTemplate ? null : "old-icons-money-bill"}
      title="Talangin"
      testId="widget-talangin"
      btnTitle="Lihat Tagihan"
      btnIcon={<FileText size={16} className="me-2" />}
      bankAmount={data?.talangin}
      isLoading={isLoading}
      isSuccess={isSuccess}
      isDisable={false}
      handleClick={() => navigate('/tagihan-talangin')}
      isProcess={lateInvoice?.allLate > 0}
      processMsg={'Ada tagihan jatuh tempo hari ini atau terlambat'}
    />
  )
}
